import { cva } from 'class-variance-authority';
import { t } from 'i18next';

import { FlowVersion, flowStructureUtil } from '@activepieces/shared';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { getPieceDisplayData } from '../lib/piece-logos.generated';

import { PieceIcon } from './piece-icon';

const extraIconVariants = cva(
  'flex items-center justify-center bg-accent/35 text-accent-foreground  p-2 rounded-full border border-solid dark:bg-accent-foreground/25 dark:text-foreground select-none',
  {
    variants: {
      size: {
        xxl: 'size-[64px]',
        xl: 'size-[48px]',
        lg: 'size-[40px]',
        md: 'size-[36px]',
        sm: 'size-[25px]',
      },
    },
    defaultVariants: {},
  },
);

export function PieceIconList({
  maxNumberOfIconsToShow,
  flowVersion,
  size,
}: {
  flowVersion: FlowVersion;
  maxNumberOfIconsToShow: number;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}) {
  const steps = flowStructureUtil.getAllSteps(flowVersion);

  const uniquePiecesMap = steps.reduce((map, step) => {
    const pieceName = step.settings.pieceName || `unknown_${step.name}`;

    const pieceData = getPieceDisplayData(pieceName);
    if (pieceData) {
      map.set(pieceName, pieceData);
    }

    return map;
  }, new Map<string, { logoUrl: string; displayName: string }>());

  const uniquePieces = [...uniquePiecesMap.values()];
  const visibleMetadata = uniquePieces.slice(0, maxNumberOfIconsToShow);
  const extraPieces = uniquePieces.length - visibleMetadata.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex gap-2">
          {visibleMetadata.map((metadata) => (
            <PieceIcon
              logoUrl={metadata.logoUrl}
              showTooltip={false}
              circle={true}
              size={size ?? 'md'}
              border={true}
              displayName={metadata.displayName}
              key={metadata.logoUrl}
            />
          ))}
          {extraPieces > 0 && (
            <div className={extraIconVariants({ size: size ?? 'md' })}>
              +{extraPieces}
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {uniquePieces.length > 1 &&
          uniquePieces
            .map((m) => m?.displayName || '')
            .slice(0, -1)
            .join(', ') +
            ` ${t('and')} ${uniquePieces[uniquePieces.length - 1].displayName}`}
        {uniquePieces.length === 1 && t(uniquePieces[0].displayName)}
      </TooltipContent>
    </Tooltip>
  );
}
